import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getAuthToken } from "../../../../util/auth";
import { getApiChara } from "../../../../util/getApi";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { FaPlay, FaEraser, FaEye, FaFileExport, FaPrint, FaCheckCircle, FaTimes, FaCalendarAlt, FaInfoCircle, FaStethoscope, FaUser, FaWhatsapp } from 'react-icons/fa';
import AuthContext from "../../../../store/auth-context";
import { getReportHeader, getReportStyles } from "../../../../util/reportPrintHelper";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../../util/headerUtil";
import { exportTableToExcel } from "../../../../util/excelExportUtil";
import { useNavigate } from "react-router-dom";
import { printDischarge, exportDischargePDF } from "../Pages/DPrint";

const IPDPatientReport = () => {
    const { t } = useTranslation();
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    };

    const getSixMonthsAgoDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 180);
        return date.toISOString().split("T")[0];
    };

    const [patients, setPatients] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("ACTIVE");

    const [startDate, setStartDate] = useState(getSixMonthsAgoDate());
    const [endDate, setEndDate] = useState(getTodayDate());
    const [selectedPageSize, setSelectedPageSize] = useState("a4");
    const [selectedOrientation, setSelectedOrientation] = useState("portrait");
    const [exportFormat, setExportFormat] = useState("excel");
    const [isExporting, setIsExporting] = useState(false);
    const [showPreview, setShowPreview] = useState(null);
    const [selectedAdmission, setSelectedAdmission] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const authCtx = useContext(AuthContext);
    const { hospitalData } = authCtx;
    const navigate = useNavigate();

    const fetchPatients = async (status = statusFilter, start = startDate, end = endDate, forceClientFilter = false) => {
        const token = getAuthToken();
        if (!token || token === "EXPIRED") return;

        setIsLoading(true);
        try {
            const apiChar = getApiChara();
            let apiUrl;
            const useDateWiseApi = ["DISCHARGED", "TRANSFERRED", "CANCELLED", "ALL"].includes(status);

            if (useDateWiseApi) {
                apiUrl = `${apiChar}/ipd/admissions/status/datewise?status=${status}&startDate=${start}&endDate=${end}`;
            } else {
                apiUrl = `${apiChar}/ipd/admissions/status/${status}`;
            }

            const response = await fetch(apiUrl, {
                headers: { Authorization: "Bearer " + token },
            });
            if (!response.ok) throw new Error("Failed to fetch patients");
            const data = await response.json();

            let result = data || [];

            if (!useDateWiseApi && forceClientFilter) {
                result = data.filter(adm => {
                    const adDate = adm.admissionDateTime ? adm.admissionDateTime.split('T')[0] : (adm.admissionDateTime ? adm.admissionDateTime.split(' ')[0] : "");
                    return adDate >= start && adDate <= end;
                });
            }

            setPatients(result || []);
        } catch (err) {
            console.error(err);
            toast.error(t("Error fetching IPD patients"));
            setPatients([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (["DISCHARGED", "TRANSFERRED", "CANCELLED", "ALL"].includes(statusFilter)) {
            const start = getSixMonthsAgoDate();
            const end = getTodayDate();
            setStartDate(start);
            setEndDate(end);
            fetchPatients(statusFilter, start, end, false);
        } else {
            fetchPatients(statusFilter, startDate, endDate, false);
        }
    }, [statusFilter]);

    const handleFilterSubmit = () => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 180) {
            toast.warning(t("Please select dates within the 180 days."));
            return;
        }
        fetchPatients(statusFilter, startDate, endDate, true);
    };

    const handleFilterClear = () => {
        const today = getTodayDate();
        const start = ["DISCHARGED", "TRANSFERRED", "CANCELLED", "ALL"].includes(statusFilter) ? getSixMonthsAgoDate() : today;
        setStartDate(start);
        setEndDate(today);
        fetchPatients(statusFilter, start, today, true);
    };

    const generatePDF = () => {
        const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
        const pageWidth = pdfDoc.internal.pageSize.getWidth();

        const startY = drawReportHeader(pdfDoc, hospitalData, `IPD Patient List - ${statusFilter}`, {
            margin: 0,
            top: 0,
            contentMargin: 40,
            hideSeparator: true,
            isExport: true
        });

        // 2. Metadata / Filter info
        pdfDoc.setFontSize(10);
        pdfDoc.setTextColor(100, 100, 100);
        pdfDoc.text(`Period: ${startDate} to ${endDate}`, 40, startY + 5);
        pdfDoc.text(`Total Records: ${patients.length}`, 40, startY + 20);
        pdfDoc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth - 40, startY + 5, { align: "right" });

        // 3. Table Generation
        pdfDoc.autoTable({
            head: [["IPD No", "Patient ID", "Name", "Gender", "Phone", "Doctor", "Floor", "Ward/Bed", "Adm Date", "Disch Date", "Status"]],
            body: patients.map((admission) => [
                admission.ipdAdmissionId || "-",
                admission.patientId?.patientId || "-",
                admission.patientId ? `${admission.patientId.firstName} ${admission.patientId.lastName}` : "-",
                admission.patientId?.gender || "-",
                admission.patientId?.mobile || "-",
                admission.doctorId?.firstName || "-",
                admission.wardBed?.floor || "-",
                admission.wardBed ? `${admission.wardBed.wardName} - ${admission.wardBed.bedNumber}` : "-",
                admission.admissionDateTime ? admission.admissionDateTime.split(/[ T]/)[0] : "-",
                admission.dischargeDateTime ? admission.dischargeDateTime.split(/[ T]/)[0] : "-",
                admission.status || "-"
            ]),
            startY: startY + 35,
            styles: {
                fontSize: 9,
                cellPadding: 6,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                halign: 'center',
                lineWidth: 0.1,
                lineColor: [0, 0, 0]
            },
            alternateRowStyles: {
                fillColor: [248, 248, 248]
            },
            footStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                lineWidth: 0.1,
                lineColor: [0, 0, 0]
            }
        });

        drawAuthorizedSignatory(pdfDoc, hospitalData, t("Authority Sign / Doctor Sign"));

        // 4. Footer
        const pageCount = pdfDoc.internal.getNumberOfPages();
        pdfDoc.setFontSize(9);
        for (let i = 1; i <= pageCount; i++) {
            pdfDoc.setPage(i);
            pdfDoc.text(`${t("Page")} ${i} ${t("of")} ${pageCount}`, pageWidth / 2, pdfDoc.internal.pageSize.getHeight() - 20, { align: "center" });
        }

        return pdfDoc;
    };

    const onExportHandler = () => {
        if (exportFormat === "excel") {
            setIsExporting(true);
            setTimeout(() => {
                exportTableToExcel("ipdPatientsTableReport", `IPD_Report_${statusFilter}`);
                setIsExporting(false);
            }, 500);
        } else {
            generatePDF().save(`IPD_Report_${statusFilter}_${getTodayDate()}.pdf`);
        }
    };

    const onPreviewHandler = () => {
        const pdfBlob = generatePDF().output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setShowPreview(pdfUrl);
    };

    const onPrintHandler = () => {
        if (!patients || patients.length === 0) {
            alert(t("No data to print"));
            return;
        }

        // Create a hidden iframe for printing
        let printFrame = document.getElementById("printFrame");
        if (!printFrame) {
            printFrame = document.createElement("iframe");
            printFrame.id = "printFrame";
            printFrame.style.display = "none";
            document.body.appendChild(printFrame);
        }

        const tableClone = document.querySelector("#ipdPatientsTableReport").cloneNode(true);
        // Remove Action column (the last column)
        tableClone.querySelectorAll('tr').forEach(tr => {
            const cells = tr.querySelectorAll('th, td');
            if (cells.length > 0) {
                cells[cells.length - 1].remove();
            }
        });

        const printContent = `
      <html>
        <head>
          <title>${t("IPD Patient List")} - ${statusFilter}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${getReportStyles(selectedPageSize, selectedOrientation)}
          </style>
        </head>
        <body>
          ${getReportHeader(hospitalData, `${t("IPD Patient Report")} - ${statusFilter}`)}
          <div class="meta-info">
            <div><strong>${t("Report Period")}:</strong> ${startDate} to ${endDate}</div>
            <div><strong>${t("Total Records")}:</strong> ${patients.length}</div>
          </div>
          <div class="summary-badge" style="background: #f8fafc; padding: 10px 15px; border-radius: 8px; border-left: 4px solid #334155; margin-bottom: 20px; font-size: 13px;">
            <strong>${t("Summary")}:</strong> ${t("Total of")} ${patients.length} ${t("patients found under category")} <strong>${statusFilter}</strong>.
          </div>
          ${tableClone.outerHTML}
        </body>
      </html>
    `;

        const frameDoc = printFrame.contentWindow.document;
        frameDoc.open();
        frameDoc.write(printContent);
        frameDoc.close();

        printFrame.contentWindow.focus();
        setTimeout(() => {
            printFrame.contentWindow.print();
        }, 500);
    };

    return (
        <div>
            {/* Date & Status Filters Row */}
            <div className="bg-light p-3 rounded mx-0 shadow-sm border mt-3 mb-4">
                <div className="row g-3 justify-content-center align-items-end">
                    <div className="col-md-auto col-6">
                        <label className="fw-bold small mb-1 text-muted">{t("Start Date")}:</label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ border: "1px solid #ced4da" }}
                        />
                    </div>
                    <div className="col-md-auto col-6">
                        <label className="fw-bold small mb-1 text-muted">{t("End Date")}:</label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ border: "1px solid #ced4da" }}
                        />
                    </div>
                    <div className="col-md-auto col-12">
                        <label className="fw-bold small mb-1 text-muted">{t("Status")}:</label>
                        <select
                            className="form-select form-select-sm themed-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="ACTIVE">{t("ACTIVE")}</option>
                            <option value="ALL">{t("ALL")}</option>
                            <option value="DISCHARGED">{t("DISCHARGED")}</option>
                            <option value="CANCELLED">{t("CANCELLED")}</option>
                            <option value="TRANSFERRED">{t("TRANSFERRED")}</option>
                        </select>
                    </div>
                    <div className="col-md-auto col-12">
                        <div className="d-flex gap-2">
                            <Button className="btn-premium flex-grow-1" size="sm" onClick={handleFilterSubmit}>
                                <FaPlay className="me-2" /> {t("SUBMIT")}
                            </Button>
                            <Button className="btn-secondary-premium flex-grow-1" size="sm" onClick={handleFilterClear}>
                                <FaEraser className="me-2" /> {t("CLEAR")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="printData boxes table-responsive mt-1">
                <Table id="ipdPatientsTableReport" striped bordered hover responsive size="sm" className="small">
                    <thead className="table-secondary">
                        <tr>
                            <th>{t("IPD No")}</th>
                            <th>{t("Patient ID")}</th>
                            <th>{t("Name")}</th>
                            <th>{t("Gender")}</th>
                            <th>{t("Phone")}</th>
                            <th>{t("Doctor")}</th>
                            <th>{t("Floor")}</th>
                            <th>{t("Ward/Bed")}</th>
                            <th>{t("Admission Date")}</th>
                            <th>{t("Discharge Date")}</th>
                            <th>{t("Status")}</th>
                            <th style={{ minWidth: '150px' }}>{t("Action")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan="9" className="text-center">
                                    <Spinner animation="border" size="sm" /> {t("Loading...")}
                                </td>
                            </tr>
                        ) : patients.length > 0 ? (
                            patients.map((admission) => (
                                <tr key={admission.ipdAdmissionId}>
                                    <td>{admission.ipdAdmissionId}</td>
                                    <td>{admission.patientId?.patientId}</td>
                                    <td>
                                        {admission.patientId
                                            ? `${admission.patientId.firstName} ${admission.patientId.lastName}`
                                            : "-"}
                                    </td>
                                    <td>{admission.patientId?.gender || "-"}</td>
                                    <td>{admission.patientId?.mobile || "-"}</td>
                                    <td>{admission.doctorId?.firstName || "-"}</td>
                                    <td>
                                        {admission.wardBed
                                            ? `${admission.wardBed.floor || "-"}`
                                            : "-"}
                                    </td>
                                    <td>
                                        {admission.wardBed
                                            ? `${admission.wardBed.wardName} - ${admission.wardBed.bedNumber}`
                                            : "-"}
                                    </td>
                                    <td>{admission.admissionDateTime ? admission.admissionDateTime.split('T')[0] : "-"}</td>
                                    <td>{admission.dischargeDateTime ? admission.dischargeDateTime.split('T')[0] : "-"} | {admission.dischargeDateTime ? admission.dischargeDateTime.split('T')[1]?.substring(0, 5) : "-"}</td>
                                    <td>
                                        <span
                                            className="badge"
                                            style={{
                                                backgroundColor: admission.status === 'ACTIVE' ? 'var(--primary-color)' :
                                                    admission.status === 'DISCHARGED' ? 'var(--secondary-color)' : '#6c757d',
                                                color: 'white'
                                            }}
                                        >
                                            {admission.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1 justify-content-center">
                                            <Button
                                                className="btn-outline-theme"
                                                size="sm"
                                                title={t("View Details")}
                                                onClick={() => {
                                                    setSelectedAdmission(admission);
                                                    setShowDetailsModal(true);
                                                }}
                                            >
                                                <FaEye />
                                            </Button>
                                            {(admission.status === "DISCHARGED" || admission.drWrittenDischargeSummery) && (
                                                <>
                                                    <Button
                                                        className="btn-outline-secondary-premium"
                                                        size="sm"
                                                        title={t("Print Summary")}
                                                        onClick={() => {
                                                            const mapped = {
                                                                patientName: `${admission.patientId?.firstName || ""} ${admission.patientId?.lastName || ""}`,
                                                                age: admission.patientId?.age || "-",
                                                                sex: t(admission.patientId?.gender) || "-",
                                                                ipdNo: admission.ipdAdmissionId,
                                                                doa: admission.admissionDateTime?.split("T")[0] || admission.admissionDateTime?.split(" ")[0] || "-",
                                                                dod: admission.dischargeDateTime?.split("T")[0] || admission.dischargeDateTime?.split(" ")[0] || "-",
                                                                address: admission.patientId?.address || "-",
                                                                regNo: admission.patientId?.patientId || "-",
                                                                ids: {
                                                                    adhar: admission.adharCardNo || admission.patientId?.adharCardNo || "-",
                                                                    pan: admission.panCardNo || admission.patientId?.panCardNo || "-"
                                                                },
                                                                diagnosis: admission.diagnosis || admission.additionReason || "-",
                                                                dischargeType: admission.dischargeType || "-",
                                                                condition: admission.conditionAtDischarge || "Stable",
                                                                drWrittenDischargeSummery: admission.drWrittenDischargeSummery || "",
                                                                weight: admission.weight || "-",
                                                                pho: admission.pho || "-",
                                                                co: admission.co || "-",
                                                                kco: admission.kco || "-",
                                                                cf: admission.cf || "-",
                                                                nextAppointment: admission.nextAppointmentDate || admission.nextFollowupVisitToDr || "-",
                                                                notes: admission.notes || "-"
                                                            };
                                                            printDischarge(mapped, authCtx, "a4");
                                                        }}
                                                    >
                                                        <FaPrint />
                                                    </Button>
                                                    <Button
                                                        className="btn-outline-export-premium"
                                                        size="sm"
                                                        title={t("Export PDF")}
                                                        onClick={() => {
                                                            const mapped = {
                                                                patientName: `${admission.patientId?.firstName || ""} ${admission.patientId?.lastName || ""}`,
                                                                age: admission.patientId?.age || "-",
                                                                sex: t(admission.patientId?.gender) || "-",
                                                                ipdNo: admission.ipdAdmissionId,
                                                                doa: admission.admissionDateTime?.split("T")[0] || admission.admissionDateTime?.split(" ")[0] || "-",
                                                                dod: admission.dischargeDateTime?.split("T")[0] || admission.dischargeDateTime?.split(" ")[0] || "-",
                                                                address: admission.patientId?.address || "-",
                                                                regNo: admission.patientId?.patientId || "-",
                                                                ids: {
                                                                    adhar: admission.adharCardNo || admission.patientId?.adharCardNo || "-",
                                                                    pan: admission.panCardNo || admission.patientId?.panCardNo || "-"
                                                                },
                                                                diagnosis: admission.diagnosis || admission.additionReason || "-",
                                                                dischargeType: admission.dischargeType || "-",
                                                                condition: admission.conditionAtDischarge || "Stable",
                                                                drWrittenDischargeSummery: admission.drWrittenDischargeSummery || "",
                                                                weight: admission.weight || "-",
                                                                pho: admission.pho || "-",
                                                                co: admission.co || "-",
                                                                kco: admission.kco || "-",
                                                                cf: admission.cf || "-",
                                                                nextAppointment: admission.nextAppointmentDate || admission.nextFollowupVisitToDr || "-",
                                                                notes: admission.notes || "-"
                                                            };
                                                            exportDischargePDF(mapped, authCtx);
                                                        }}
                                                    >
                                                        <FaFileExport />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center text-danger fw-bold">
                                    {t("Record not found")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Bottom Print Options */}
            {patients.length > 0 && (
                <div className="row g-3 justify-content-center align-items-end pt-3 pb-3 bg-light rounded mx-0 mt-3 border shadow-sm">
                    <div className="col-auto">
                        <label className="fw-bold small d-block">{t("Select Size")}</label>
                        <select
                            className="form-select themed-select"
                            value={selectedPageSize}
                            onChange={(e) => setSelectedPageSize(e.target.value)}
                            style={{ width: '100px' }}
                        >
                            <option value="a2">A2</option>
                            <option value="a3">A3</option>
                            <option value="a4">A4</option>
                            <option value="a5">A5</option>
                        </select>
                    </div>

                    <div className="col-auto">
                        <label className="fw-bold small d-block">{t("Orientation")}</label>
                        <select
                            className="form-select themed-select"
                            value={selectedOrientation}
                            onChange={(e) => setSelectedOrientation(e.target.value)}
                            style={{ width: '130px' }}
                        >
                            <option value="portrait">{t("Portrait")}</option>
                            <option value="landscape">{t("Landscape")}</option>
                        </select>
                    </div>

                    <div className="col-auto">
                        <Button className="btn-premium" onClick={onPrintHandler}>
                            <FaPrint className="me-2" /> {t("PRINT")}
                        </Button>
                    </div>

                    <div className="col-auto">
                        <Button className="btn-premium" onClick={onPreviewHandler}>
                            <FaEye className="me-2" /> {t("PREVIEW")}
                        </Button>
                    </div>

                    <div className="col-auto">
                        <label className="fw-bold small d-block">{t("Format")}</label>
                        <select
                            className="form-select themed-select"
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value)}
                            style={{ width: '140px' }}
                        >
                            <option value="pdf">{t("PDF Format")}</option>
                            <option value="excel">{t("Excel Format")}</option>
                        </select>
                    </div>

                    <div className="col-auto">
                        <Button
                            className="btn-export-premium d-flex align-items-center"
                            onClick={onExportHandler}
                            disabled={isExporting}
                        >
                            {isExporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FaFileExport className="me-2" />}
                            {isExporting ? t("EXPORTING...") : t("EXPORT")}
                        </Button>
                    </div>
                </div>
            )}

            {/* Patient Details Modal */}
            {showDetailsModal && selectedAdmission && (
                <div
                    className="modal fade show shadow-premium"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1050 }}
                    tabIndex="-1"
                    role="dialog"
                >
                    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
                        <div className="modal-content border-0 overflow-hidden rounded-4">
                            <div className="modal-header bg-premium text-white py-3">
                                <h5 className="modal-title d-flex align-items-center">
                                    <FaEye className="me-2" /> {t("IPD ADMISSION DETAILS")} - {selectedAdmission.ipdAdmissionId}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)} aria-label={t("Close")}></button>
                            </div>

                            <div className="modal-body p-4 bg-light">
                                <div className="row g-4">
                                    {/* 1. Patient Profile Section */}
                                    <div className="col-lg-4">
                                        <div className="card border-0 shadow-sm h-100 rounded-4 overflow-hidden">
                                            <div className="card-header bg-premium text-white py-3">
                                                <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                                    <FaUser /> {t("Patient Profile")}
                                                </h6>
                                            </div>
                                            <div className="card-body p-4 text-center">
                                                <div className="avatar-circle mx-auto mb-3 bg-premium text-white d-flex align-items-center justify-content-center fw-bold shadow-sm" style={{ width: '80px', height: '80px', borderRadius: '50%', fontSize: '32px' }}>
                                                    {selectedAdmission.patientId?.firstName?.charAt(0)}{selectedAdmission.patientId?.lastName?.charAt(0)}
                                                </div>
                                                <h5 className="mb-0 fw-bold text-dark">{selectedAdmission.patientId?.firstName} {selectedAdmission.patientId?.lastName}</h5>
                                                <span className="text-muted small">{t("PID")}: {selectedAdmission.patientId?.patientId}</span>

                                                <div className="mt-4 pt-2 text-start">
                                                    <div className="d-flex justify-content-between mb-2 pb-1 border-bottom border-light">
                                                        <span className="text-muted small">{t("Gender")} / {t("Age")}</span>
                                                        <span className="fw-semibold small">{t(selectedAdmission.patientId?.gender) || '-'} / {selectedAdmission.patientId?.age || '-'} {t("Yrs")}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-2 pb-1 border-bottom border-light">
                                                        <span className="text-muted small">{t("Mobile No")}</span>
                                                        <span className="fw-bold text-teal small">{selectedAdmission.patientId?.mobile || '-'}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-2 pb-1 border-bottom border-light">
                                                        <span className="text-muted small">{t("Blood Group")}</span>
                                                        <span className="fw-bold text-teal small">{selectedAdmission.bloodGroup || selectedAdmission.patientId?.bloodGroup || '-'}</span>
                                                    </div>
                                                    <div className="mb-3 mt-3">
                                                        <label className="text-muted small d-block mb-1">{t("Address")}</label>
                                                        <div className="p-2 bg-light rounded small text-dark border-start border-3 border-teal">
                                                            {selectedAdmission.patientId?.address || '-'}
                                                        </div>
                                                    </div>
                                                    <div className="row mt-3">
                                                        <div className="col-6">
                                                            <label className="text-muted small d-block mb-1">{t("Aadhar No")}</label>
                                                            <span className="small fw-semibold text-dark">{selectedAdmission.adharCardNo || selectedAdmission.patientId?.adharCardNo || '-'}</span>
                                                        </div>
                                                        <div className="col-6">
                                                            <label className="text-muted small d-block mb-1">{t("PAN No")}</label>
                                                            <span className="small fw-semibold text-uppercase text-dark">{selectedAdmission.panCardNo || selectedAdmission.patientId?.panCardNo || '-'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2. Admission & Clinical Section */}
                                    <div className="col-lg-8">
                                        {/* Admission Highlights */}
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-3">
                                                <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100 text-center">
                                                    <label className="text-muted small d-block mb-1">{t("IPD Number")}</label>
                                                    <h5 className="mb-0 fw-bold text-dark">{selectedAdmission.ipdAdmissionId}</h5>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100 text-center">
                                                    <label className="text-muted small d-block mb-1">{t("Ward / Bed")}</label>
                                                    <h6 className="mb-0 fw-bold text-dark">{selectedAdmission.wardBed ? `${selectedAdmission.wardBed.wardName} - ${selectedAdmission.wardBed.bedNumber}` : '-'}</h6>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100 text-center">
                                                    <label className="text-muted small d-block mb-1">{t("Room Floor")}</label>
                                                    <h6 className="mb-0 fw-bold text-dark">{selectedAdmission.wardBed?.floor || '-'}</h6>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100 text-center">
                                                    <label className="text-muted small d-block mb-1">{t("Status")}</label>
                                                    <h6 className="mb-0 fw-bold text-teal">{t(selectedAdmission.status)}</h6>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                                            <div className="card-body p-4">
                                                <div className="row g-4">
                                                    <div className="col-md-6 border-end border-light">
                                                        <h6 className="fw-bold text-teal border-bottom pb-2 mb-3 d-flex align-items-center gap-2">
                                                            <FaCalendarAlt /> {t("Admission Details")}
                                                        </h6>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted small">{t("Date & Time")}</span>
                                                            <span className="fw-semibold small">{selectedAdmission.admissionDateTime || '-'}</span>
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="text-muted small d-block mb-1">{t("Consulting Doctor")}</label>
                                                            <span className="fw-bold text-dark small">Dr. {selectedAdmission.doctorId?.firstName} {selectedAdmission.doctorId?.lastName} ({selectedAdmission.doctorId?.degree})</span>
                                                        </div>
                                                        <div className="mb-2">
                                                            <label className="text-muted small d-block mb-1">{t("Admission Reason")}</label>
                                                            <p className="small text-dark mb-0">{selectedAdmission.admissionReason || '-'}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <h6 className="fw-bold text-teal border-bottom pb-2 mb-3 d-flex align-items-center gap-2">
                                                            <FaInfoCircle /> {t("Guardian Info")}
                                                        </h6>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted small">{t("Name")}</span>
                                                            <span className="fw-semibold small">{selectedAdmission.guardianName || '-'}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted small">{t("Relation")}</span>
                                                            <span className="fw-semibold small">{t(selectedAdmission.guardianRelation) || '-'}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted small">{t("Contact")}</span>
                                                            <span className="fw-bold text-teal small">{selectedAdmission.guardianContactNumber || '-'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mt-4 pt-4 border-top">
                                                    <h6 className="fw-bold text-teal mb-3 d-flex align-items-center gap-2">
                                                        <FaStethoscope /> {t("Clinical Information")}
                                                    </h6>
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="text-muted small d-block mb-1">{t("Diagnosis")}</label>
                                                            <div className="p-2 rounded bg-teal text-white small fw-bold text-center shadow-sm">
                                                                {selectedAdmission.diagnosis || t('No Diagnosis Recorded')}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-8">
                                                            <label className="text-muted small d-block mb-1">{t("Symptoms")} ({selectedAdmission.symptomsTitle || t('General')})</label>
                                                            <div className="p-2 rounded bg-light small text-dark border">
                                                                {selectedAdmission.symptomsDesc || t('No symptoms described')}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Added Clinical Fields */}
                                                    <div className="row g-3 mt-1">
                                                        <div className="col-md-6">
                                                            <label className="text-muted small d-block mb-1">{t("Personal History")}</label>
                                                            <div className="p-2 rounded bg-white small text-dark border">
                                                                {selectedAdmission.pho || '-'}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="text-muted small d-block mb-1">{t("Complaint Of")}</label>
                                                            <div className="p-2 rounded bg-white small text-dark border">
                                                                {selectedAdmission.co || '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row g-3 mt-1">
                                                        <div className="col-md-6">
                                                            <label className="text-muted small d-block mb-1">{t("Known Case Of")}</label>
                                                            <div className="p-2 rounded bg-white small text-dark border">
                                                                {selectedAdmission.kco || '-'}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="text-muted small d-block mb-1">{t("Clinical Findings")}</label>
                                                            <div className="p-2 rounded bg-white small text-dark border">
                                                                {selectedAdmission.cf || '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Vitals Section */}
                                                <div className="mt-4 p-3 bg-light rounded-4 border border-teal-subtle">
                                                    <div className="row text-center g-2">
                                                        <div className="col">
                                                            <label className="text-muted d-block small mb-0">{t("BP")}</label>
                                                            <span className="fw-bold text-teal">{selectedAdmission.bp || '-'}</span>
                                                        </div>
                                                        <div className="col">
                                                            <label className="text-muted d-block small mb-0">{t("Pulse")}</label>
                                                            <span className="fw-bold text-teal">{selectedAdmission.pulse || '-'}</span>
                                                        </div>
                                                        <div className="col">
                                                            <label className="text-muted d-block small mb-0">{t("Temp")}</label>
                                                            <span className="fw-bold text-teal">{selectedAdmission.temperature || '-'}°F</span>
                                                        </div>
                                                        <div className="col">
                                                            <label className="text-muted d-block small mb-0">{t("SpO2")}</label>
                                                            <span className="fw-bold text-teal">{selectedAdmission.spo2 || '-'}%</span>
                                                        </div>
                                                        <div className="col">
                                                            <label className="text-muted d-block small mb-0">{t("Wt")}</label>
                                                            <span className="fw-bold text-teal">{selectedAdmission.weight || '-'} kg</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Discharge Section */}
                                        <div className="card border-0 shadow-sm rounded-4 border-top border-5 border-teal">
                                            <div className="card-header bg-white py-3">
                                                <h6 className="mb-0 fw-bold text-teal d-flex align-items-center gap-2">
                                                    <FaCheckCircle /> {t("Discharge & Outcome")}
                                                </h6>
                                            </div>
                                            <div className="card-body p-4">
                                                <div className="row mb-3 pb-3 border-bottom border-light g-4">
                                                    <div className="col-md-6 border-end border-light">
                                                        <label className="text-muted small d-block mb-1">{t("Discharge Date & Time")}</label>
                                                        <span className="fw-bold text-danger">{selectedAdmission.dischargeDateTime || t('PENDING')}</span>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="text-muted small d-block mb-1">{t("TPA / Insurance Info")}</label>
                                                        <span className="small text-dark fw-bold">{selectedAdmission.tpaId ? `${selectedAdmission.tpaId} (${t("Expires")}: ${selectedAdmission.tpaValidity || 'N/A'})` : t('Cash Patient')}</span>
                                                    </div>
                                                </div>

                                                {selectedAdmission.drWrittenDischargeSummery && (
                                                    <div className="mt-4">
                                                        <label className="text-teal fw-bold small d-block mb-2">{t("Doctor's Clinical Summary & Treatment")}</label>
                                                        <div
                                                            className="p-3 bg-white rounded border shadow-sm"
                                                            style={{ minHeight: "150px", maxHeight: "400px", overflowY: "auto", borderLeft: "4px solid #006d77" }}
                                                            dangerouslySetInnerHTML={{ __html: selectedAdmission.drWrittenDischargeSummery }}
                                                        />
                                                    </div>
                                                )}

                                                {selectedAdmission.notes && (
                                                    <div className="mt-3 bg-light p-3 rounded-3 border-start border-4 border-secondary">
                                                        <label className="text-muted small d-block mb-1 fw-bold">{t("Additional Notes / Remarks")}:</label>
                                                        <p className="small mb-0 text-dark italic">{selectedAdmission.notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer bg-white p-3 border-top justify-content-between">
                                <div className="d-flex gap-2">
                                    {(selectedAdmission.status === "DISCHARGED" || selectedAdmission.drWrittenDischargeSummery) && (
                                        <>
                                            <Button
                                                className="btn-premium px-4"
                                                onClick={() => {
                                                    const mapped = {
                                                        patientName: `${selectedAdmission.patientId?.firstName || ""} ${selectedAdmission.patientId?.lastName || ""}`,
                                                        age: selectedAdmission.patientId?.age || "-",
                                                        sex: t(selectedAdmission.patientId?.gender) || "-",
                                                        ipdNo: selectedAdmission.ipdAdmissionId,
                                                        doa: selectedAdmission.admissionDateTime?.split(" ")[0] || "-",
                                                        doaTime: selectedAdmission.admissionDateTime?.split(" ")[1] || "",
                                                        dod: selectedAdmission.dischargeDateTime?.split(" ")[0] || "-",
                                                        dodTime: selectedAdmission.dischargeDateTime?.split(" ")[1] || "",
                                                        address: selectedAdmission.patientId?.address || "-",
                                                        regNo: selectedAdmission.patientId?.patientId || "-",
                                                        ids: {
                                                            adhar: selectedAdmission.adharCardNo || selectedAdmission.patientId?.adharCardNo || "-",
                                                            pan: selectedAdmission.panCardNo || selectedAdmission.patientId?.panCardNo || "-"
                                                        },
                                                        diagnosis: selectedAdmission.diagnosis || selectedAdmission.additionReason || "-",
                                                        dischargeType: selectedAdmission.dischargeType || "-",
                                                        condition: selectedAdmission.conditionAtDischarge || "Stable",
                                                        drWrittenDischargeSummery: selectedAdmission.drWrittenDischargeSummery || "",
                                                        nextAppointment: selectedAdmission.nextAppointmentDate || "-",
                                                        notes: selectedAdmission.notes || "-",
                                                        weight: selectedAdmission.weight || "-",
                                                        pho: selectedAdmission.pho || "-",
                                                        co: selectedAdmission.co || "-",
                                                        kco: selectedAdmission.kco || "-",
                                                        cf: selectedAdmission.cf || "-"
                                                    };
                                                    printDischarge(mapped, authCtx, "a4");
                                                }}
                                            >
                                                <FaPrint className="me-2" /> {t("PRINT SUMMARY")}
                                            </Button>
                                            <Button
                                                className="btn-outline-premium px-4 border-2"
                                                onClick={() => {
                                                    const mapped = {
                                                        patientName: `${selectedAdmission.patientId?.firstName || ""} ${selectedAdmission.patientId?.lastName || ""}`,
                                                        age: selectedAdmission.patientId?.age || "-",
                                                        sex: t(selectedAdmission.patientId?.gender) || "-",
                                                        ipdNo: selectedAdmission.ipdAdmissionId,
                                                        doa: selectedAdmission.admissionDateTime?.split(" ")[0] || "-",
                                                        doaTime: selectedAdmission.admissionDateTime?.split(" ")[1] || "",
                                                        dod: selectedAdmission.dischargeDateTime?.split(" ")[0] || "-",
                                                        dodTime: selectedAdmission.dischargeDateTime?.split(" ")[1] || "",
                                                        address: selectedAdmission.patientId?.address || "-",
                                                        regNo: selectedAdmission.patientId?.patientId || "-",
                                                        ids: {
                                                            adhar: selectedAdmission.adharCardNo || selectedAdmission.patientId?.adharCardNo || "-",
                                                            pan: selectedAdmission.panCardNo || selectedAdmission.patientId?.panCardNo || "-"
                                                        },
                                                        diagnosis: selectedAdmission.diagnosis || selectedAdmission.additionReason || "-",
                                                        dischargeType: selectedAdmission.dischargeType || "-",
                                                        condition: selectedAdmission.conditionAtDischarge || "Stable",
                                                        drWrittenDischargeSummery: selectedAdmission.drWrittenDischargeSummery || "",
                                                        nextAppointment: selectedAdmission.nextAppointmentDate || "-",
                                                        notes: selectedAdmission.notes || "-",
                                                        weight: selectedAdmission.weight || "-",
                                                        pho: selectedAdmission.pho || "-",
                                                        co: selectedAdmission.co || "-",
                                                        kco: selectedAdmission.kco || "-",
                                                        cf: selectedAdmission.cf || "-"
                                                    };
                                                    exportDischargePDF(mapped, authCtx);
                                                }}
                                            >
                                                <FaFileExport className="me-2" /> {t("EXPORT PDF")}
                                            </Button>
                                        </>
                                    )}
                                </div>
                                <Button className="btn-premium shadow-sm px-5" onClick={() => setShowDetailsModal(false)}>
                                    <FaTimes className="me-2" /> {t("CLOSE DETAILS")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Preview Modal */}
            {showPreview && (
                <div
                    className="modal fade show"
                    style={{ display: "block", backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
                    tabIndex="-1"
                    role="dialog"
                >
                    <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
                        <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
                            <div className="modal-header bg-premium text-white py-3">
                                <h5 className="modal-title d-flex align-items-center">
                                    <FaEye className="me-2" /> {t("IPD PATIENT REPORT PREVIEW")}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowPreview(null)}
                                    aria-label={t("Close")}
                                ></button>
                            </div>
                            <div className="modal-body p-0 bg-dark">
                                <iframe
                                    title="PDF Preview"
                                    src={showPreview}
                                    width="100%"
                                    height="700"
                                    frameBorder="0"
                                    className="w-100"
                                ></iframe>
                            </div>
                            <div className="modal-footer bg-light p-3">
                                <Button className="btn-secondary-premium px-4" onClick={() => setShowPreview(null)}>
                                    {t("CLOSE PREVIEW")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IPDPatientReport;
